import React, { useState } from "react";
import { sectionStyles } from "./commonStyles";
import { EventDetails, Header, HeroSection, VendorsCollaborators, WeddingStyle, ConfirmationPage, YourBudget, YourDetails, Disclaimer } from './sections';
import { INIT_FORM_DATA, MAIN_AND_OTHER_FIELDS, OPTIONAL_FIELDS, QUESTIONNAIRE_SUCCESS_TEXT } from "./constants";
import emailjs from '@emailjs/browser';

const WeddingForm = () => {
  const [formData, setFormData] = useState(INIT_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "date") {
      const minDate = new Date().toISOString().split("T")[0]; // Today's date in YYYY-MM-DD format
      if (value < minDate) {
        alert("Invalid date! Please select a date today or later.");
        setFormData((prev) => ({
          ...prev,
          [name]: "", // Reset the invalid value
        }));
        return;
      }
    }
  
    if (type === "checkbox") {
      setFormData((prev) => {
        const currentValues = prev[name] ? prev[name].split(", ") : [];
        const updatedValues = checked
          ? [...currentValues, value]
          : currentValues.filter((v) => v !== value);
  
        const updatedFormData = {
          ...prev,
          [name]: updatedValues.join(", "),
        };

        if (name === "vendorsAndCollaborators") {
          validateField("checkbox", name, updatedFormData[name]);
        }
  
        return updatedFormData;
      });
      console.log(formData);
    } else {
      setFormData((prev) => {
        const updatedFormData = {
          ...prev,
          [name]: value,
        };
  
        const fieldGroup = MAIN_AND_OTHER_FIELDS.find(
          ({ main, other }) => name === main || name === other
        );
        if (fieldGroup) {
          validateFieldWithOther(fieldGroup.main, fieldGroup.other, updatedFormData);
        } else {
          validateField(type, name, value);
        }
  
        return updatedFormData;
      });
    }
  };
  
  const validateFieldWithOther = (mainField, otherField, updatedFormData = formData) => {
    const mainValue = updatedFormData[mainField];
    const otherValue = updatedFormData[otherField];
  
    if (!mainValue && !otherValue) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [mainField]: "Please select an option or specify other",
      }));
      return false;
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [mainField]: "",
      }));
      return true; 
    }
  };  

  const validateField = (type, name, value) => {
    let error = "";

    if (!value && OPTIONAL_FIELDS.includes(name)) {
      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors[name]; // Remove error if present
        return updatedErrors;
      });
      return;
    }

    if (!value) {
      error = "This field is required";
    } else {
      switch (type) {
        case "email":
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            error = "Please enter a valid email address";
          }
          break;
        default:
          break;
      }
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const handleSuccessChange = () => {
    setSuccess(false);
    setFormData(INIT_FORM_DATA);
    setErrors({});
  }

  const handleSubmit = (e) => {
    e.preventDefault();
  
     // Initialize a new errors object
    const newErrors = {};

    // Validate all field groups (main and other)
    MAIN_AND_OTHER_FIELDS.forEach(({ main, other }) => {
      const mainValue = formData[main];
      const otherValue = formData[other];

      if (!mainValue && !otherValue) {
        newErrors[main] = "Please select an option or specify other";
      }
    });

    // Validate standalone fields (not part of MAIN_AND_OTHER_FIELDS)
    Object.keys(formData).forEach((field) => {
      if (
        !OPTIONAL_FIELDS.includes(field) &&
        !MAIN_AND_OTHER_FIELDS.some(({ main, other }) => field === main || field === other) &&
        !formData[field]
      ) {
        newErrors[field] = "This field is required";
      }

      // Additional validation for specific types (e.g., email)
      if (field.includes("Email") && formData[field]) {
        validateField("email", field, formData[field]);
      }
    });

    // Update errors state
    setErrors(newErrors);

    // Prevent submission if there are errors
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    function formatDate(dateStr) {
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Mesecevi brojevi idu od 0 do 11
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
    
    // Formatiraj pre slanja
    formData.primaryDate = formatDate(formData.primaryDate);
    formData.alternativeDates = formatDate(formData.alternativeDates);

    // emailjs integration (uncomment if needed)
    emailjs.send('service_3yzrm8g', 'template_1q529yh', formData, '-jfoZiMzgV2ZXxhqB')
      .then((result) => {
        setSuccess(true);
      })
      .catch((error) => {
        setSuccess(false);
      });
  };
  

  return success ? (
    <ConfirmationPage successText={QUESTIONNAIRE_SUCCESS_TEXT} handleResubmit={handleSuccessChange} />
  ) : (
    <>
      <HeroSection />
      <form onSubmit={handleSubmit} className="max-w-screen-lg mx-auto bg-white rounded-md">
      {/* Section: Your Details */}
      <YourDetails
        handleChange={handleChange}
        sectionStyles={sectionStyles}
        errors={errors}
        formData={formData}
      />

      {/* Section: Event Details */}
      <EventDetails
        handleChange={handleChange}
        sectionStyles={sectionStyles}
        errors={errors}
        formData={formData}
      />

      {/* Section: Your Budget */}
      <YourBudget
        handleChange={handleChange}
        sectionStyles={sectionStyles}
        errors={errors}
        formData={formData}
      />

      {/* Section: Vendors & Collaborators */}
      <VendorsCollaborators
        handleChange={handleChange}
        sectionStyles={sectionStyles}
        errors={errors}
        formData={formData}
      />

      {/* Section: Wedding Style */}
      <WeddingStyle
        handleChange={handleChange}
        sectionStyles={sectionStyles}
        errors={errors}
        formData={formData}
      />

      {/* Submit Button */}
      <button
        type="submit"
        className="my-5 bg-platinum text-black py-2 px-4 rounded hover:bg-platinum-600 focus:outline-none mx-auto block"
      >
        Submit
      </button>
    </form>
    <Header />
    <Disclaimer />
    </>
    
  );
};

export default WeddingForm;
